import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import CultureHero from './components/CultureHero';
import CultureMediaSection from './components/CultureMediaSection';
import UniverseSection from './components/UniverseSection';
import { cultureUniverses } from '@/mocks/culture';

function Culture() {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const sectionIds = cultureUniverses.map((u) => u.id);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <CultureHero />

        {/* Pub */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <AdsBanner pageKey="culture" />
        </section>

        <CultureMediaSection />

        {/* Sticky universe nav */}
        <div className="sticky top-16 md:top-20 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="flex items-center gap-2 overflow-x-auto py-3 no-scrollbar">
              {cultureUniverses.map((u) => (
                <button
                  key={u.id}
                  onClick={() => scrollToId(u.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    activeId === u.id
                      ? 'bg-primary-500 text-foreground-950'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-50'
                  }`}
                >
                  <i className={`${u.icon} text-sm`} />
                  {u.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Universes */}
        {cultureUniverses.map((universe, index) => (
          <UniverseSection key={universe.id} universe={universe} index={index} />
        ))}

        {/* CTA */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12 text-center">
            <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-3">
              Rejoignez la communauté
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Vous êtes un créateur, un artiste ou un artisan ?
            </h2>
            <p className="text-base md:text-lg text-foreground-400 max-w-2xl mx-auto mb-8">
              Mettez votre savoir-faire en avant et touchez un public qui aime la culture.
              Rejoignez l&apos;annuaire WorldApeiron et faites rayonner votre talent.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/directory"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
              >
                Explorer l&apos;annuaire
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="/auth/register"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300/60 text-foreground-50 font-semibold text-sm hover:bg-background-200/50 transition-colors whitespace-nowrap"
              >
                Créer mon profil
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default Culture;